import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plus,
  LogOut,
  Images,
  Eye,
  Pencil,
  Link2,
  Trash2,
  Loader2,
  Globe,
  Lock,
  Presentation,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { mediaUrl } from "@/lib/api";
import { copyText } from "@/lib/clipboard";

const CARD_INITIAL = { opacity: 0, y: 14 };
const CARD_ANIMATE = { opacity: 1, y: 0 };

function StatusBadge({ isPublic }) {
  return isPublic ? (
    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] font-semibold px-2.5 py-1 rounded-full bg-green-500/10 text-green-400 border border-green-500/20">
      <Globe className="h-3 w-3" /> Public
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] font-semibold px-2.5 py-1 rounded-full bg-slate-500/10 text-slate-300 border border-slate-500/20">
      <Lock className="h-3 w-3" /> Private
    </span>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [items, setItems] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/presentations");
      setItems(data);
    } catch (e) {
      toast.error(apiError(e, "Failed to load presentations"));
      setItems([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createPresentation = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/presentations", { title, description });
      toast.success("Presentation created");
      setCreateOpen(false);
      setTitle("");
      setDescription("");
      navigate(`/admin/presentations/${data.id}`);
    } catch (err) {
      toast.error(apiError(err, "Could not create presentation"));
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async (p) => {
    // const url = `${window.location.origin}/presentation/${p.slug}`;
    const url = `${window.location.origin}/ppt`;
    const ok = await copyText(url);
    if (!ok) {
      toast.error("Couldn't copy automatically. Link: " + url);
      return;
    }
    if (p.is_public) toast.success("Public link copied to clipboard");
    else toast.warning("Link copied — make it public so viewers can open it");
  };

  const confirmDelete = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    if (!target) return;
    try {
      await api.delete(`/presentations/${target.id}`);
      toast.success("Presentation deleted");
      setItems((prev) => prev.filter((x) => x.id !== target.id));
    } catch (e) {
      toast.error(apiError(e, "Failed to delete"));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background bg-grid">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/70 border-b border-white/10">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo className="h-7" />
            <span className="hidden sm:block h-6 w-px bg-white/10" />
            <span className="hidden sm:block font-heading text-sm font-medium text-white/80 tracking-tight">
              Presentation Gallery
            </span>
          </div>
          <Button
            data-testid="logout-btn"
            variant="ghost"
            onClick={handleLogout}
            className="text-muted-foreground hover:text-white hover:bg-white/10 rounded-full"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-screen-2xl mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-brand-blue font-semibold mb-2">
              EntroLabs Portal
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-white">
              Presentation Gallery
            </h1>
            <p className="text-muted-foreground mt-2 max-w-xl">
              Upload high-resolution slides, arrange them, and share a clean full-screen
              presentation with a single link.
            </p>
          </div>
          <Button
            data-testid="create-presentation-btn"
            onClick={() => setCreateOpen(true)}
            className="rounded-full bg-brand-blue hover:bg-brand-bluehover text-white h-11 px-6 font-medium transition-all hover:-translate-y-0.5 shrink-0"
          >
            <Plus className="h-4 w-4" /> New Presentation
          </Button>
        </div>

        {items === null ? (
          <div className="flex items-center justify-center py-32">
            <Loader2 className="h-7 w-7 animate-spin text-brand-blue" />
          </div>
        ) : items.length === 0 ? (
          <EmptyState onCreate={() => setCreateOpen(true)} />
        ) : (
          <div
            data-testid="presentations-grid"
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
          >
            {items.map((p, i) => (
              <motion.div
                key={p.id}
                data-testid={`presentation-card-${p.id}`}
                initial={CARD_INITIAL}
                animate={CARD_ANIMATE}
                transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.3) }}
                className="group rounded-2xl border border-white/10 bg-card overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.35)] hover:border-brand-blue/50 hover:-translate-y-1 transition-all duration-300"
              >
                <button
                  data-testid={`preview-card-${p.id}`}
                  onClick={() => navigate(`/admin/presentations/${p.id}/preview`)}
                  className="block w-full aspect-video bg-black/40 relative overflow-hidden"
                >
                  {p.cover_thumbnail_url ? (
                    <img
                      src={mediaUrl(p.cover_thumbnail_url)}
                      alt={p.title}
                      className="w-full h-full object-contain"
                      draggable={false}
                    />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-white/25">
                      <Images className="h-10 w-10" />
                    </div>
                  )}
                  <div className="absolute top-3 left-3">
                    <StatusBadge isPublic={p.is_public} />
                  </div>
                </button>

                <div className="p-5">
                  <h3 className="font-heading text-lg font-semibold text-white tracking-tight truncate">
                    {p.title}
                  </h3>
                  <div className="mt-1.5 flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Images className="h-3.5 w-3.5" /> {p.slide_count} slides
                    </span>
                    {p.updated_at && (
                      <span>Updated {format(new Date(p.updated_at), "MMM d, yyyy")}</span>
                    )}
                  </div>

                  <div className="mt-5 flex items-center gap-2">
                    <Button
                      data-testid={`preview-btn-${p.id}`}
                      variant="secondary"
                      size="sm"
                      onClick={() => navigate(`/admin/presentations/${p.id}/preview`)}
                      className="rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white"
                    >
                      <Eye className="h-4 w-4" /> Preview
                    </Button>
                    <Button
                      data-testid={`edit-btn-${p.id}`}
                      variant="secondary"
                      size="sm"
                      onClick={() => navigate(`/admin/presentations/${p.id}`)}
                      className="rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white"
                    >
                      <Pencil className="h-4 w-4" /> Edit
                    </Button>
                    <Button
                      data-testid={`copy-link-btn-${p.id}`}
                      variant="secondary"
                      size="sm"
                      onClick={() => copyLink(p)}
                      className="rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white"
                    >
                      <Link2 className="h-4 w-4" /> Copy Link
                    </Button>
                    <Button
                      data-testid={`delete-btn-${p.id}`}
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteTarget(p)}
                      className="ml-auto rounded-full text-muted-foreground hover:text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-card border-white/10 text-white" data-testid="create-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Create presentation</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Give your presentation a name. You can upload slides on the next screen.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={createPresentation} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="p-title" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
                Presentation name
              </Label>
              <Input
                id="p-title"
                data-testid="create-title-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Annual Holiday Presentation"
                required
                className="bg-background/60 border-white/10 h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="p-desc" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
                Description (optional)
              </Label>
              <Textarea
                id="p-desc"
                data-testid="create-description-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="A short summary of this presentation"
                className="bg-background/60 border-white/10"
              />
            </div>
            <DialogFooter>
              <Button
                data-testid="create-submit-btn"
                type="submit"
                disabled={creating}
                className="rounded-full bg-brand-blue hover:bg-brand-bluehover text-white"
              >
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create & upload"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-card border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete presentation?</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              "{deleteTarget?.title}" and all its slides will be permanently removed. This cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full bg-white/5 border-white/10 text-white hover:bg-white/10">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-btn"
              onClick={confirmDelete}
              className="rounded-full bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div
      data-testid="empty-state"
      className="rounded-2xl border border-dashed border-white/15 bg-white/[0.02] py-20 flex flex-col items-center text-center px-6"
    >
      <div className="h-16 w-16 rounded-2xl bg-brand-blue/10 border border-brand-blue/20 grid place-items-center mb-5">
        <Presentation className="h-8 w-8 text-brand-blue" />
      </div>
      <h3 className="font-heading text-xl font-semibold text-white">No presentations yet</h3>
      <p className="text-muted-foreground mt-2 max-w-sm">
        Create your first presentation, upload slides, and generate a shareable link.
      </p>
      <Button
        onClick={onCreate}
        className="mt-6 rounded-full bg-brand-blue hover:bg-brand-bluehover text-white h-11 px-6"
      >
        <Plus className="h-4 w-4" /> New Presentation
      </Button>
    </div>
  );
}
